import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { ImpactRow, StickyFeatures } from '@/components/landing/service'
import { BRAND, halftone } from '@/components/landing/service/halftone'
import { UseCaseVisual } from './fragments'
import { HighlightHeading, PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, muted, type LayoutProps } from './shared'

type Tile = { name: string; note: string; app?: OfficeApp }

/**
 * Exactly what the product connects to today. Office apps use their marks;
 * everything else is a plain wordmark tile (no third-party logos).
 */
const CONNECTOR_GROUPS: Array<{ title: string; tone: 'white' | 'blue' | 'purple' | 'pink' | 'dark'; tiles: Tile[] }> = [
  {
    title: 'Microsoft 365 add-ins',
    tone: 'white',
    tiles: [
      { name: 'Word', note: 'Add-in', app: 'word' },
      { name: 'Excel', note: 'Add-in', app: 'excel' },
      { name: 'PowerPoint', note: 'Add-in', app: 'powerpoint' },
      { name: 'Outlook', note: 'Add-in', app: 'outlook' },
    ],
  },
  { title: 'CRM', tone: 'blue', tiles: [{ name: 'HubSpot', note: 'Contacts, companies, deals' }] },
  {
    title: 'Data connectors',
    tone: 'purple',
    tiles: [
      { name: 'SharePoint', note: 'Lists' },
      { name: 'Excel Online', note: 'Workbooks' },
      { name: 'SQL database', note: 'Queries' },
      { name: 'REST API', note: 'Custom endpoints' },
    ],
  },
  { title: 'Notifications', tone: 'pink', tiles: [{ name: 'Slack', note: 'Channel updates' }] },
  {
    title: 'AI model providers',
    tone: 'dark',
    tiles: [
      { name: 'OpenRouter', note: 'Models' },
      { name: 'OpenAI', note: 'Models' },
      { name: 'Anthropic', note: 'Models' },
    ],
  },
]

const TONE: Record<(typeof CONNECTOR_GROUPS)[number]['tone'], string> = {
  white: 'bg-[#f3f3f3] text-[#0a0a0a] dark:bg-[#1c1c26] dark:text-white',
  blue: 'bg-[var(--color-landing-accent)] text-white',
  purple: 'bg-[var(--color-landing-secondary)] text-white',
  pink: 'bg-[#ff00f2] text-[#0a0a0a]',
  dark: 'bg-[#0a0a0a] text-white dark:bg-black',
}

function ConnectorTile({ tile, tone }: { tile: Tile; tone: keyof typeof TONE }) {
  return (
    <div className={cn('relative flex aspect-[4/3] flex-col justify-between overflow-hidden p-5 md:p-6', TONE[tone])}>
      {tone !== 'white' && tone !== 'dark' && <span aria-hidden className="absolute inset-0" style={halftone(0.18, 6)} />}
      {tile.app ? (
        <OfficeAppIcon app={tile.app} className="relative h-10 w-10 md:h-12 md:w-12" />
      ) : (
        <span aria-hidden className="relative block h-2 w-10 bg-current opacity-60" />
      )}
      <span className="relative">
        <span className="block font-[family-name:var(--font-landing-display)] text-[clamp(1rem,1.6vw,1.5rem)] uppercase leading-[1.3]">
          {tile.name}
        </span>
        <span className="mt-1 block text-sm opacity-75">{tile.note}</span>
      </span>
    </div>
  )
}

/** A Word document whose fields are filled from named sources (decorative). */
function FieldMap() {
  const rows = [
    { field: 'Client name', value: 'Northwind Ltd', source: 'HubSpot', color: BRAND.blue },
    { field: 'Deal value', value: '£48,000', source: 'HubSpot', color: BRAND.blue },
    { field: 'Price table', value: '12 lines', source: 'SQL database', color: BRAND.purple },
    { field: 'Standard terms', value: 'v2026.3', source: 'SharePoint', color: BRAND.purple },
    { field: 'Approval sent', value: '#proposals', source: 'Slack', color: BRAND.pink },
  ]
  return (
    <div aria-hidden className="relative aspect-square overflow-hidden bg-[var(--color-landing-accent)] [container-type:inline-size]">
      <span className="absolute inset-0" style={halftone(0.22, 7)} />
      <div className="absolute left-[9%] right-[9%] top-[12%] bg-white text-[clamp(10px,2.2cqw,16px)] text-[#0a0a0a] shadow-[0_40px_70px_-30px_rgba(0,0,40,0.65)] dark:bg-[#1c1c26] dark:text-white">
        <div className={cn('flex items-center gap-[0.6em] border-b px-[1.2em] py-[0.8em]', hairline)}>
          <OfficeAppIcon app="word" className="h-[1.8em] w-[1.8em]" />
          <span className="font-semibold">Proposal · Northwind</span>
          <span className="ml-auto text-[0.85em] text-[var(--color-landing-gray-500)]">Fields filled</span>
        </div>
        <ul className="px-[1.2em] py-[0.6em]">
          {rows.map((r) => (
            <li key={r.field} className={cn('grid grid-cols-[1fr_1fr_auto] items-center gap-[0.8em] border-b py-[0.7em] last:border-b-0', hairline)}>
              <span className="text-[var(--color-landing-gray-500)]">{r.field}</span>
              <span className="font-semibold">{r.value}</span>
              <span className="px-[0.5em] py-[0.15em] text-[0.8em] font-bold uppercase text-white" style={{ backgroundColor: r.color }}>
                {r.source}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * Integrations ("sticky-split"): the hero copy holds still on the left while
 * a tall stack of the real connectors scrolls past on the right; then an
 * image + text block (a document filling from its sources, bleeding off the
 * left edge, with the capabilities beside it); use cases; impact; FAQ;
 * purple CTA.
 */
export function IntegrationsLayout({ feature }: LayoutProps) {
  return (
    <>
      {/* 1. Sticky split hero */}
      <section aria-labelledby="integrations-title" className="pb-20 pt-16 md:pt-24 lg:pb-32">
        <div className="landing-container grid grid-cols-1 gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-20">
          <div className="lg:sticky lg:top-[110px] lg:self-start">
            <HeroReveal delay={0}>
              <p className="landing-text-display-lg">
                <span className="inline-block -rotate-1 bg-[var(--color-landing-secondary)] px-[0.3em] text-white">
                  {feature.shortLabel}.
                </span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 id="integrations-title" className={cn('landing-text-hero-sm mt-6', ink)}>
                <span className="block">{feature.heroLines[0]}</span>{' '}
                <span className="block">{feature.heroLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <p className="landing-text-body-lg mt-8 max-w-[52ch] text-pretty border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)] md:text-xl">
                {feature.subheadline}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <TrialButton />
                <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep">
                  Book a demo
                </Link>
              </div>
              <dl className={cn('mt-12 grid grid-cols-3 gap-4 border-t pt-8 md:gap-8', hairline)}>
                {feature.heroStats.map((stat) => (
                  <div key={stat.label} className="flex min-w-0 flex-col-reverse justify-end">
                    <dt className={cn('mt-2 text-sm leading-snug md:text-base', muted)}>{stat.label}</dt>
                    <dd className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,3.4vw,3rem)] uppercase leading-[1.3]', ink)}>
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </HeroReveal>
          </div>

          <div className="grid gap-12">
            {CONNECTOR_GROUPS.map((group) => (
              <div key={group.title}>
                <h2 className={cn('font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em]', ink)}>
                  {group.title}
                </h2>
                <StaggerContainer amount={0.15} className="mt-4 grid grid-cols-2 gap-3">
                  {group.tiles.map((tile) => (
                    <StaggerItem key={tile.name}>
                      <ConnectorTile tile={tile} tone={group.tone} />
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Image + text: the graphic bleeds off the left edge */}
      <section aria-labelledby="integrations-value-title" className="landing-section overflow-x-clip bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="mx-auto grid max-w-[120rem] items-start gap-12 lg:grid-cols-12 lg:gap-0">
          <ScrollReveal direction="none" className="lg:sticky lg:top-[110px] lg:col-span-6">
            <FieldMap />
          </ScrollReveal>
          <div className="px-[clamp(1.5rem,2vw,2rem)] lg:col-span-5 lg:col-start-8 lg:pl-0">
            <HighlightHeading id="integrations-value-title" text={feature.valueProposition.title} color="purple" className="landing-text-display-lg" />
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg mt-6 max-w-[48ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
            <StaggerContainer amount={0.1} className="mt-10 grid">
              {feature.capabilities.map((cap) => (
                <StaggerItem key={cap.title} className={cn('border-t py-5', hairline)}>
                  <h3 className={cn('font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase tracking-[-0.01em]', ink)}>
                    {cap.title}
                  </h3>
                  <p className={cn('landing-text-body-sm mt-1.5 text-pretty', muted)}>{cap.description}</p>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* 3. Use cases, pinned */}
      <section aria-labelledby="integrations-usecases-title" className="landing-section">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
            <span id="integrations-usecases-title">How teams use {feature.shortLabel}</span>
          </TextReveal>
        </div>
        <StickyFeatures
          accent="purple"
          steps={feature.useCases.map((useCase) => ({
            title: useCase.title,
            description: useCase.description,
            extra: (
              <span className="inline-block bg-[var(--color-landing-primary)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
                {useCase.industry}
              </span>
            ),
            visual: (active: boolean) => <UseCaseVisual useCase={useCase} active={active} />,
          }))}
        />
      </section>

      {/* 4. Impact */}
      <section aria-labelledby="integrations-impact-title" className="landing-section pt-0">
        <div className="landing-container">
          <h2 id="integrations-impact-title" className={cn('mb-10 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4]', ink)}>
            Business impact
          </h2>
          <ImpactRow benefits={feature.benefits} />
        </div>
      </section>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="purple" />
    </>
  )
}
